import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { consultationFormOptions } from '@/mocks/consultationData';
import { localizedPath } from '@/lib/contactApi';

interface FormData {
  name: string;
  furigana: string;
  email: string;
  phone: string;
  consultationMethod: string;
  preferredDate1: string;
  preferredDate2: string;
  content: string;
  privacyAgreed: boolean;
}

const initialFormData: FormData = {
  name: '',
  furigana: '',
  email: '',
  phone: '',
  consultationMethod: '',
  preferredDate1: '',
  preferredDate2: '',
  content: '',
  privacyAgreed: false,
};

export default function ConsultationForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('consultation.form.error.name');
    if (!formData.furigana.trim()) newErrors.furigana = t('consultation.form.error.furigana');
    if (!formData.email.trim()) {
      newErrors.email = t('consultation.form.error.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('consultation.form.error.emailInvalid');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('consultation.form.error.phone');
    }
    if (!formData.consultationMethod) newErrors.consultationMethod = t('consultation.form.error.method');
    if (!formData.preferredDate1.trim()) newErrors.preferredDate1 = t('consultation.form.error.date1');
    if (formData.content.length > 500) {
      newErrors.content = t('consultation.form.error.content');
    }
    if (!formData.privacyAgreed) {
      newErrors.privacyAgreed = t('consultation.form.error.privacy');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      navigate(localizedPath('/consultation/confirm'), { state: { formData } });
    }
  };

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/40" id="consultation-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('consultation.form.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('consultation.form.title')}
          </h2>
          <p className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">
            {t('consultation.form.titleEn')}
          </p>
          <div className="gold-line mx-auto mb-5"></div>
          <p className="text-[15px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            <span className="text-accent-600 text-[12px] mr-1">{t('consultation.form.requiredBadge')}</span>
            {t('consultation.form.requiredNote')}
          </p>
        </div>

        {/* Alternative contact */}
        <div className="text-center mb-10 bg-background-50 border border-background-200/60 p-5">
          <p className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            {t('consultation.form.altContact')}{' '}
            <a href="tel:0368244086" className="text-foreground-800 font-medium hover:text-accent-600 transition-colors">
              03-6824-4086
            </a>
            {t('consultation.form.altContactHours')}{' '}
            <a href="mailto:info@butler-academy.jp" className="text-accent-600 hover:text-accent-500 transition-colors">
              info@butler-academy.jp
            </a>
            {t('consultation.form.altContactSuffix')}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-7">
            {/* Name */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.nameLabel')} <span className="text-accent-600 text-[12px]">{t('consultation.form.requiredBadge')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('consultation.form.namePlaceholder')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            {/* Furigana */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.furiganaLabel')} <span className="text-accent-600 text-[12px]">{t('consultation.form.requiredBadge')}</span>
              </label>
              <input
                type="text"
                name="furigana"
                value={formData.furigana}
                onChange={(e) => updateField('furigana', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('consultation.form.furiganaPlaceholder')}
              />
              {errors.furigana && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.furigana}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.emailLabel')} <span className="text-accent-600 text-[12px]">{t('consultation.form.requiredBadge')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('consultation.form.emailPlaceholder')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.phoneLabel')} <span className="text-accent-600 text-[12px]">{t('consultation.form.requiredBadge')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('consultation.form.phonePlaceholder')}
              />
              {errors.phone && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.phone}</p>
              )}
            </div>

            {/* Consultation Method */}
            <div>
              <p className="text-[14px] text-foreground-700 tracking-[0.06em] mb-3 font-label">
                {t('consultation.form.methodLabel')} <span className="text-accent-600 text-[12px]">{t('consultation.form.requiredBadge')}</span>
              </p>
              <div className="space-y-2">
                {consultationFormOptions.methods.map((method) => (
                  <label
                    key={method.value}
                    className="flex items-start gap-3 p-4 bg-background-50 border border-background-200/60 hover:border-accent-500/30 cursor-pointer transition-colors duration-200"
                  >
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                          formData.consultationMethod === method.value
                            ? 'border-accent-500'
                            : 'border-background-300'
                        }`}
                      >
                        {formData.consultationMethod === method.value && (
                          <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                        )}
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="consultationMethod"
                      value={method.value}
                      checked={formData.consultationMethod === method.value}
                      onChange={(e) => updateField('consultationMethod', e.target.value)}
                      className="sr-only"
                    />
                    <div>
                      <span className="block text-[16px] text-foreground-800 tracking-[0.04em] mb-1">{t(`consultation.form.method.${method.value}.label`)}</span>
                      <span className="block text-[14px] text-foreground-500 tracking-[0.03em]">{t(`consultation.form.method.${method.value}.desc`)}</span>
                      {method.value === 'visit' && formData.consultationMethod === 'visit' && (
                        <Link
                          to="/access"
                          className="inline-block text-[14px] text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors mt-2"
                        >
                          {t('consultation.form.accessLink')}
                        </Link>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              {errors.consultationMethod && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.consultationMethod}</p>
              )}
            </div>

            {/* Preferred Date 1 */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.date1Label')} <span className="text-accent-600 text-[12px]">{t('consultation.form.requiredBadge')}</span>
              </label>
              <input
                type="text"
                name="preferredDate1"
                value={formData.preferredDate1}
                onChange={(e) => updateField('preferredDate1', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('consultation.form.date1Placeholder')}
              />
              {errors.preferredDate1 && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.preferredDate1}</p>
              )}
            </div>

            {/* Preferred Date 2 */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.date2Label')} <span className="text-foreground-400 text-[12px]">{t('consultation.form.optionalBadge')}</span>
              </label>
              <input
                type="text"
                name="preferredDate2"
                value={formData.preferredDate2}
                onChange={(e) => updateField('preferredDate2', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('consultation.form.date2Placeholder')}
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('consultation.form.contentLabel')} <span className="text-foreground-400 text-[12px]">{t('consultation.form.optionalBadge')}</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={(e) => updateField('content', e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400 resize-none"
                placeholder={t('consultation.form.contentPlaceholder')}
              ></textarea>
              <p className="text-[13px] text-foreground-400 mt-1 text-right">
                {formData.content.length}/500
              </p>
              {errors.content && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.content}</p>
              )}
            </div>

            {/* Privacy Policy */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="privacyAgreed"
                  checked={formData.privacyAgreed}
                  onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-colors duration-200 ${
                    formData.privacyAgreed
                      ? 'bg-accent-500 border-accent-500'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {formData.privacyAgreed && (
                    <i className="ri-check-line text-[12px] text-primary-900"></i>
                  )}
                </div>
                <span className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
                  <Link to="/privacy" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors">
                    {t('consultation.form.privacyLink')}
                  </Link>
                  {t('consultation.form.privacyLabel')}
                  <span className="text-accent-600 text-[12px] ml-1">{t('consultation.form.requiredBadge')}</span>
                </span>
              </label>
              {errors.privacyAgreed && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.privacyAgreed}</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('consultation.form.submitBtn')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
